/**
 * 業務・業務ルール（F3）のルートパス定義。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務詳細と業務ルールのルートパスと、ロジック図の見本（v2 ・ v3）と業務の旧 URL（業務ロジック ・ データの流れ）を定義する。
 * @graph-connects none
 */
import type { FeatureRouteSets } from './feature-route-sets.js';

/**
 * 業務詳細 — 業務 1 件を、入力 ・ 業務ルール ・ 業務ルールの出力 ・ 分岐 ・ 作用 ・ 終了 の 6 種類の節点の
 * ロジック図（データの流れの図）で見る（2026-10-01 に 3 種類の節点の図から置き換えた）。
 *
 * 業務ルールの見方はこの図 1 つに寄せた（Dhik 2026-09-30「業務ルールの view が 3 種類あるが、フロー図の
 * ものになったので、他の view を消して URL を寄せてほしい」）。前の業務詳細（入出力の欄 ・ 使われている
 * 場所）・ 業務ロジック（一覧）・ 受け入れ条件の試し（v2）は消し、それぞれの旧 URL はここへ倒す。
 * 業務の番号はパスパラメータなので文字列で届き、受け口（`BusinessDataflowRoutePage`）が数に直す。
 * 案件は `?pj=`、左右比較を閉じたことは `?compare=0` で受ける。
 * @graph-connects none
 */
export const BUSINESS_DETAIL_ROUTE = '/businesses/$businessId';

/**
 * 業務のロジック図の見本 v2 — マッチ度を業務ルールで表す案（mock）。ナビにもリンクにも載せず、
 * URL を直に打ったときだけ入る（Dhik 2026-10-01「ボタンは作らなくていいよ。直接 URL 書き換えるため」）。
 * 受け入れ条件の試し（旧 v2）の転送は、この案で置き換えた。
 * @graph-connects none
 */
export const BUSINESS_DETAIL_V2_ROUTE = '/businesses/$businessId/v2';

/**
 * 業務のロジック図の見本 v3 — 判断をすべて分岐でつないだ木（mock）。v2 と同じく URL を直に打った
 * ときだけ入る。
 * @graph-connects none
 */
export const BUSINESS_DETAIL_V3_ROUTE = '/businesses/$businessId/v3';

/**
 * 旧 業務ロジック（業務ルールの一覧）。業務詳細へ倒す（`LegacyBusinessRedirect`）。
 * @graph-connects none
 */
export const BUSINESS_LOGIC_ROUTE = '/businesses/$businessId/logic';

/**
 * 旧 業務のデータの流れ。図は業務詳細の URL へ移ったので、業務詳細へ倒す（`LegacyBusinessRedirect`）。
 * @graph-connects none
 */
export const BUSINESS_DATAFLOW_ROUTE = '/businesses/$businessId/dataflow';

/**
 * 業務ルール 1 件。業務ルールは業務から独立しているので、業務の下に置かない。案件は `?pj=` で受ける。
 * @graph-connects none
 */
export const RULE_ROUTE = '/rules/$ruleId';

/**
 * 業務・業務ルールのルートの集合。業務詳細と業務ルールは案件を検索条件で受ける。見本（v2 ・ v3）と
 * 旧 URL の倒し先（業務ロジック ・ データの流れ）は検索条件を検証しない（`flowRouteSets` の旧ストーリーと同じ）。
 * @graph-connects none
 */
export const businessLogicRouteSets = {
  all: [
    BUSINESS_DETAIL_ROUTE,
    BUSINESS_DETAIL_V2_ROUTE,
    BUSINESS_DETAIL_V3_ROUTE,
    BUSINESS_LOGIC_ROUTE,
    BUSINESS_DATAFLOW_ROUTE,
    RULE_ROUTE,
  ],
  pjSearch: [BUSINESS_DETAIL_ROUTE, RULE_ROUTE],
  operationSearch: [],
  pjPath: [],
} as const satisfies FeatureRouteSets;
